import { Injectable } from "@angular/core";
import { DecisionAction, DecisionDetailItem, DecisionListItem, DecisionMissingInformation } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { DecisionActionLike, DecisionUrgencyTone, DecisionUxSubject } from "./home-decision-action.types";

@Injectable({ providedIn: "root" })
export class HomeDecisionActionPresentationRules {
  private readonly lowConfidenceThreshold = 0.8;
  private readonly dueSoonMs = 1000 * 60 * 60 * 72;

  constructor(private readonly lang: LanguageService) {}

  toDecisionSubject(
    decision: Pick<DecisionListItem | DecisionDetailItem, "state" | "dueAt" | "confidence" | "priority" | "risk"> & {
      missingInformationCount?: number;
      missingInformation?: readonly DecisionMissingInformation[];
      sourceTrace?: DecisionUxSubject["sourceTrace"];
    } | null
  ): DecisionUxSubject | null {
    return decision
      ? {
          state: decision.state,
          dueAt: decision.dueAt,
          missingInformationCount: decision.missingInformationCount,
          missingInformation: decision.missingInformation,
          confidence: decision.confidence,
          priority: decision.priority,
          risk: decision.risk,
          sourceTrace: decision.sourceTrace
        }
      : null;
  }

  getMissingInformationReason(missingInformation?: readonly DecisionMissingInformation[]): string | null {
    if (!missingInformation?.length) {
      return null;
    }

    const requiredItem = missingInformation.find((item) => item.requiredForAction && !!item.reason);
    if (requiredItem?.reason) {
      return requiredItem.reason;
    }

    const firstWithReason = missingInformation.find((item) => !!item.reason);
    return firstWithReason?.reason ?? null;
  }

  getLowConfidenceReason(confidence: DecisionUxSubject["confidence"]): string | null {
    const warning = confidence.warnings.find((item) => item.trim().length > 0);
    if (warning) {
      return warning;
    }

    return confidence.overall < this.lowConfidenceThreshold
      ? this.lang.translate("home.decisionActionUx.lowConfidenceReason")
      : null;
  }

  hasCatalogDegradation(
    sourceTrace: DecisionUxSubject["sourceTrace"],
    action: DecisionActionLike | null,
    confidence: DecisionUxSubject["confidence"]
  ): boolean {
    const catalogHealth = JSON.stringify(sourceTrace?.catalogHealthAtEvaluation ?? "").toLowerCase();
    const warningText = confidence.warnings.join(" ").toLowerCase();
    const disabledReason = action?.disabledReason?.toLowerCase() ?? "";
    return /(catalog|degrad|fallback|unavailable|stale|outdated|health)/.test(`${catalogHealth} ${warningText} ${disabledReason}`);
  }

  hasPayloadSchema(action: DecisionActionLike | null): action is DecisionAction {
    return !!action && "payloadSchema" in action && action.payloadSchema !== undefined && action.payloadSchema !== null;
  }

  getActionDescription(action: DecisionActionLike | null): string | null {
    return action && "description" in action ? action.description ?? null : null;
  }

  buildUrgencyPresentation(decision: DecisionUxSubject | null): { label: string; tone: DecisionUrgencyTone } {
    if (!decision) {
      return {
        label: this.lang.translate("home.nextBestDecision.urgency.low"),
        tone: "low"
      };
    }

    const dueAtMs = decision.dueAt ? Date.parse(decision.dueAt) : Number.NaN;
    const now = Date.now();
    const isOverdue = !Number.isNaN(dueAtMs) && dueAtMs < now;
    const isDueSoon = !Number.isNaN(dueAtMs) && dueAtMs - now <= this.dueSoonMs;
    const riskLevel = decision.risk.level?.toLowerCase();

    if (decision.state === "act_now" || isOverdue || decision.priority.score >= 90 || riskLevel === "critical") {
      return { label: this.lang.translate("home.nextBestDecision.urgency.critical"), tone: "critical" };
    }

    if (decision.state === "act_soon" || isDueSoon || decision.priority.score >= 75 || riskLevel === "high") {
      return { label: this.lang.translate("home.nextBestDecision.urgency.high"), tone: "high" };
    }

    if (decision.priority.score >= 45 || decision.state === "waiting") {
      return { label: this.lang.translate("home.nextBestDecision.urgency.medium"), tone: "medium" };
    }

    return { label: this.lang.translate("home.nextBestDecision.urgency.low"), tone: "low" };
  }
}
